import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Space, Typography, Divider, Tag, Alert, Table, message, Spin, Row, Col } from 'antd';
import { SaveOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { appointmentApi, Appointment, AppointmentConflict, AppointmentPart } from '../../services/appointmentApi';
import { getTokens } from '../../services/authApi';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import './AppointmentModal.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface AppointmentModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  defaultDate?: Dayjs;
  defaultTechnicianId?: number;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Vehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  vin: string;
  customer_id: number;
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  visible,
  appointment,
  onClose,
  defaultDate,
  defaultTechnicianId
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<AppointmentConflict[]>([]);
  const [parts, setParts] = useState<AppointmentPart[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  const isEditMode = !!appointment?.id;

  useEffect(() => {
    if (visible) {
      loadTechnicians();
      loadCustomers();
      if (appointment) {
        loadAppointmentDetails();
      } else {
        resetForm();
      }
    }
  }, [visible, appointment]);

  useEffect(() => {
    if (selectedCustomerId) {
      loadCustomerVehicles(selectedCustomerId);
    } else {
      setVehicles([]);
    }
  }, [selectedCustomerId]);

  const resetForm = () => {
    form.setFieldsValue({
      appointment_type: 'diagnosis',
      status: 'scheduled',
      duration_minutes: 60,
      travel_time_minutes: 0,
      buffer_time_minutes: 15,
      manual_override: false,
      start_time: defaultDate || dayjs().add(1, 'day').hour(9).minute(0),
      technician_id: defaultTechnicianId || undefined
    });
    setConflicts([]);
    setParts([]);
    setShowConflicts(false);
  };

  const loadAppointmentDetails = async () => {
    if (!appointment?.id) return;

    setLoading(true);
    try {
      const fullAppointment = await appointmentApi.getById(appointment.id);

      form.setFieldsValue({
        ...fullAppointment,
        start_time: dayjs(fullAppointment.start_time),
        end_time: dayjs(fullAppointment.end_time)
      });

      setSelectedCustomerId(fullAppointment.customer_id);
      if (fullAppointment.conflicts) {
        setConflicts(fullAppointment.conflicts);
        setShowConflicts(fullAppointment.has_conflict);
      }
      if (fullAppointment.parts) {
        setParts(fullAppointment.parts);
      }
    } catch (error) {
      console.error('Error loading appointment details:', error);
      message.error('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      // Get auth token
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      // Fetch both technicians and owners
      const [techniciansResponse, ownersResponse] = await Promise.all([
        axios.get('/api/users', {
          params: { role: 'technician', status: 'active' },
          headers
        }),
        axios.get('/api/users', {
          params: { role: 'owner', status: 'active' },
          headers
        })
      ]);

      // Combine and sort by name
      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      // Get auth token
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const response = await axios.get('/api/customers', {
        params: { status: 'active' },
        headers
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCustomerVehicles = async (customerId: number) => {
    try {
      // Get auth token
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const response = await axios.get(`/api/customers/${customerId}/vehicles`, {
        headers
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleCheckConflicts = async () => {
    const values = form.getFieldsValue();

    if (!values.technician_id || !values.start_time) {
      message.warning('Please select technician and start time first');
      return;
    }

    const durationMinutes = values.duration_minutes || 60;
    const startTime = values.start_time;
    const endTime = startTime.clone().add(durationMinutes, 'minutes');

    setCheckingConflicts(true);
    try {
      const result = await appointmentApi.checkConflicts({
        technician_id: values.technician_id,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        appointment_id: appointment?.id
      });

      setConflicts(result.conflicts);
      setShowConflicts(true);

      if (result.has_conflicts) {
        if (result.can_override) {
          message.warning(`Found ${result.conflicts.length} conflict(s) that can be overridden`);
        } else {
          message.error('Found critical conflicts that cannot be overridden');
        }
      } else {
        message.success('No conflicts found - this time slot is available');
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
      message.error('Failed to check for conflicts');
    } finally {
      setCheckingConflicts(false);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const durationMinutes = values.duration_minutes;
      const startTime = values.start_time.toISOString();
      const endTime = values.start_time.clone().add(durationMinutes, 'minutes').toISOString();

      const appointmentData: any = {
        ...values,
        start_time: startTime,
        end_time: endTime,
        manual_override: values.manual_override || false,
        has_conflict: conflicts.length > 0,
        conflict_acknowledged: values.manual_override && conflicts.length > 0,
        customer_notified: false,
        customer_confirmed: false,
        reminder_sent: false,
        waiting_on_parts: parts.some(p => p.status !== 'received' && p.status !== 'installed')
      };

      if (isEditMode) {
        await appointmentApi.update(appointment.id!, appointmentData);
        message.success('Appointment updated successfully');
      } else {
        await appointmentApi.create(appointmentData);
        message.success('Appointment created successfully');
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      if (error.response?.status === 409) {
        message.error('Cannot save appointment due to conflicts. Enable manual override if necessary.');
      } else {
        message.error('Failed to save appointment');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!appointment?.id) return;

    Modal.confirm({
      title: 'Cancel Appointment',
      content: (
        <div>
          <p>Are you sure you want to cancel this appointment?</p>
          <Input.TextArea
            placeholder="Cancellation reason (optional)"
            id="cancellation-reason"
          />
        </div>
      ),
      onOk: async () => {
        try {
          const reason = (document.getElementById('cancellation-reason') as HTMLTextAreaElement)?.value;
          await appointmentApi.cancel(appointment.id!, 1, reason); // TODO: Get actual user ID
          message.success('Appointment cancelled');
          onClose();
        } catch (error) {
          console.error('Error cancelling appointment:', error);
          message.error('Failed to cancel appointment');
        }
      }
    });
  };

  const handleComplete = async () => {
    if (!appointment?.id) return;

    try {
      await appointmentApi.complete(appointment.id);
      message.success('Appointment marked as complete');
      onClose();
    } catch (error) {
      console.error('Error completing appointment:', error);
      message.error('Failed to complete appointment');
    }
  };

  const handleAcknowledgeConflicts = async () => {
    if (!appointment?.id) return;

    Modal.confirm({
      title: 'Acknowledge Conflicts',
      content: (
        <div>
          <p>Please provide a reason for overriding these conflicts:</p>
          <Input.TextArea
            placeholder="Override reason (required)"
            id="override-reason"
          />
        </div>
      ),
      onOk: async () => {
        try {
          const reason = (document.getElementById('override-reason') as HTMLTextAreaElement)?.value;
          if (!reason) {
            message.error('Override reason is required');
            return;
          }
          await appointmentApi.acknowledgeConflicts(appointment.id!, 1, reason); // TODO: Get actual user ID
          message.success('Conflicts acknowledged');
          loadAppointmentDetails();
        } catch (error) {
          console.error('Error acknowledging conflicts:', error);
          message.error('Failed to acknowledge conflicts');
        }
      }
    });
  };

  const conflictColumns = [
    {
      title: 'Type',
      dataIndex: 'conflict_type',
      key: 'conflict_type',
      render: (type: string) => (
        <Tag color="red">{type.replace(/_/g, ' ').toUpperCase()}</Tag>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'conflict_severity',
      key: 'conflict_severity',
      render: (severity: string) => (
        <Tag color={
          severity === 'critical' ? 'red' :
          severity === 'high' ? 'orange' :
          severity === 'medium' ? 'gold' : 'blue'
        }>
          {severity.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Status',
      dataIndex: 'resolution_status',
      key: 'resolution_status',
      render: (status: string) => (
        <Tag color={
          status === 'resolved' ? 'green' :
          status === 'acknowledged' ? 'blue' : 'default'
        }>
          {status.toUpperCase()}
        </Tag>
      )
    }
  ];

  const partsColumns = [
    {
      title: 'Part Name',
      dataIndex: 'part_name',
      key: 'part_name'
    },
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={
          status === 'received' || status === 'installed' ? 'green' :
          status === 'in_transit' ? 'blue' :
          status === 'ordered' ? 'orange' : 'default'
        }>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Expected Delivery',
      dataIndex: 'expected_delivery_date',
      key: 'expected_delivery_date',
      render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A'
    }
  ];

  return (
    <Modal
      title={isEditMode ? 'Edit Appointment' : 'New Appointment'}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      className="appointment-modal"
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          {/* Basic Information */}
          <Title level={5}>Basic Information</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Customer"
                name="customer_id"
                rules={[{ required: true, message: 'Please select customer' }]}
              >
                <Select
                  showSearch
                  placeholder="Select customer"
                  onChange={setSelectedCustomerId}
                  filterOption={(input, option) => {
                    const label = `${option?.label || ''}`.toLowerCase();
                    return label.includes(input.toLowerCase());
                  }}
                  options={customers.map(customer => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.last_name} - ${customer.phone}`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Vehicle"
                name="vehicle_id"
                rules={[{ required: true, message: 'Please select vehicle' }]}
              >
                <Select
                  placeholder="Select vehicle"
                  disabled={!selectedCustomerId}
                >
                  {vehicles.map(vehicle => (
                    <Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Employee"
                name="technician_id"
                rules={[{ required: true, message: 'Please select employee' }]}
              >
                <Select
                  placeholder="Select employee"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                  }
                  options={technicians.map(tech => ({
                    value: tech.id,
                    label: `${tech.first_name} ${tech.last_name} (${tech.role === 'owner' ? 'Owner' : 'Technician'})`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Appointment Type"
                name="appointment_type"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select>
                  <Option value="diagnosis">Diagnosis</Option>
                  <Option value="repair">Repair</Option>
                  <Option value="inspection">Inspection</Option>
                  <Option value="pickup">Pickup</Option>
                  <Option value="delivery">Delivery</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: 'Please enter title' }]}
              >
                <Input placeholder="e.g., Engine Diagnosis" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
              >
                <Select>
                  <Option value="scheduled">Scheduled</Option>
                  <Option value="confirmed">Confirmed</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                  <Option value="no_show">No Show</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={3} placeholder="Appointment details" />
          </Form.Item>

          <Divider />

          {/* Scheduling */}
          <Title level={5}>Scheduling</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Time"
                name="start_time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Duration (minutes)"
                name="duration_minutes"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <Input type="number" min={15} step={15} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Travel Time (minutes)"
                name="travel_time_minutes"
              >
                <Input type="number" min={0} step={5} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Buffer Time (minutes)"
                name="buffer_time_minutes"
              >
                <Input type="number" min={0} step={5} />
              </Form.Item>
            </Col>
          </Row>

          <Space style={{ marginBottom: 16 }}>
            <Button
              type="default"
              icon={<WarningOutlined />}
              onClick={handleCheckConflicts}
              loading={checkingConflicts}
            >
              Check for Conflicts
            </Button>
          </Space>

          {/* Conflicts */}
          {showConflicts && conflicts.length > 0 && (
            <>
              <Alert
                type="warning"
                message={`${conflicts.length} conflict(s) detected`}
                description="Review conflicts below. You may need to enable manual override to proceed."
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Table
                columns={conflictColumns}
                dataSource={conflicts}
                rowKey="id"
                pagination={false}
                size="small"
                style={{ marginBottom: 16 }}
              />
              {isEditMode && appointment?.has_conflict && !appointment?.conflict_acknowledged && (
                <Button
                  type="primary"
                  danger
                  onClick={handleAcknowledgeConflicts}
                  style={{ marginBottom: 16 }}
                >
                  Acknowledge & Override Conflicts
                </Button>
              )}
            </>
          )}

          {conflicts.length > 0 && !isEditMode && (
            <Form.Item
              label="Manual Override"
              name="manual_override"
              valuePropName="checked"
            >
              <Select>
                <Option value={false}>No - Respect conflicts</Option>
                <Option value={true}>Yes - Override conflicts</Option>
              </Select>
            </Form.Item>
          )}

          {conflicts.length > 0 && (
            <Form.Item
              label="Override Reason"
              name="override_reason"
              rules={[
                { required: form.getFieldValue('manual_override'), message: 'Please provide reason for override' }
              ]}
            >
              <TextArea rows={2} placeholder="Explain why you're overriding the conflicts" />
            </Form.Item>
          )}

          <Divider />

          {/* Parts (if editing) */}
          {isEditMode && parts.length > 0 && (
            <>
              <Title level={5}>Parts</Title>
              <Table
                columns={partsColumns}
                dataSource={parts}
                rowKey="id"
                pagination={false}
                size="small"
                style={{ marginBottom: 16 }}
              />
              <Divider />
            </>
          )}

          {/* Notes */}
          <Title level={5}>Additional Notes</Title>
          <Form.Item
            label="Customer Notes"
            name="notes"
          >
            <TextArea rows={2} placeholder="Notes visible to customer" />
          </Form.Item>

          <Form.Item
            label="Internal Notes"
            name="internal_notes"
          >
            <TextArea rows={2} placeholder="Internal notes (not visible to customer)" />
          </Form.Item>

          <Divider />

          {/* Footer Actions */}
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              {isEditMode && appointment?.status !== 'cancelled' && (
                <>
                  {appointment?.status !== 'completed' && (
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleComplete}
                    >
                      Mark Complete
                    </Button>
                  )}
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    onClick={handleCancel}
                  >
                    Cancel Appointment
                  </Button>
                </>
              )}
            </Space>
            <Space>
              <Button onClick={onClose}>Close</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                {isEditMode ? 'Update' : 'Create'} Appointment
              </Button>
            </Space>
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};
